import { NiUtils } from "./ni";

// Estende o protótipo do Array para adicionar o método {random}
// O método {random} retorna um elemento aleatório do {array} ou {undefined} se o mesmo estiver vazio
declare global {
  interface Array<T> {
    random(): T | undefined;
  }
}

export function loadArrayRandom() {
  if (!Array.prototype.random) {
    Array.prototype.random = function <T>(): T | undefined {
      if (this.length === 0) {
        return undefined;
      }

      const indice = Math.floor(Math.random() * this.length);
      return this[indice];
    };
  }
}

export function toTitleCase(s: string) {
  return s.replace(
    /\w\S*/g,
    text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
}

export class RandomUtils {
  /**
   * Gera um CPF aleatório com DV
   * @param raiz Raiz do CPF (9 primeiros dígitos)
   * @returns CPF completo (11 dígitos)
   */
  public static cpf(raiz?: number | string): string {
    if (raiz == null) {
      raiz = Math.floor(Math.random() * 1e9);
    }

    return NiUtils.obterNiCompleto(raiz, "cpf");
  }

  /**
   * Gera um CNPJ aleatório com DV
   * @param raiz Raiz do CNPJ (12 primeiros dígitos)
   * @returns CNPJ completo (14 dígitos)
   */
  public static cnpj(raiz?: number | string, isMatriz: boolean = false): string {
    if (raiz == null) {
      raiz = Math.floor(Math.random() * 1e8) * 10000;
      const unidade = isMatriz ? 1 : Math.floor(Math.random() * 10000);
      raiz += unidade;
    }

    return NiUtils.obterNiCompleto(raiz, "cnpj");
  }

  public static data(inicio?: Date, fim?: Date): Date {
    if (inicio == null) {
      inicio = new Date(1e12);
    }

    if (fim == null) {
      fim = new Date();
    }

    const inicioMs = inicio.getTime();
    const fimMs = fim.getTime();
    const dataAleatoriaMs = Math.floor(Math.random() * (fimMs - inicioMs + 1)) + inicioMs;
    return new Date(dataAleatoriaMs);
  }

  public static racional(inicio?: number, fim?: number, decimais: number = 2): number {
    if (inicio == null) {
      inicio = 0;
    }

    if (fim == null) {
      fim = 1e9;
    }

    if (inicio > fim) {
      throw new Error("O valor inicial deve ser menor ou igual ao valor final.");
    }

    if (decimais < 0) {
      throw new Error("O número de casas decimais não pode ser negativo.");
    }

    let fatorAjuste = 1;

    if (decimais > 0) {
      fatorAjuste = Math.pow(10, decimais);

      inicio = inicio * fatorAjuste;
      fim = fim * fatorAjuste;
    }

    const valor = Math.floor(Math.random() * (fim - inicio + 1)) + inicio;

    return valor / fatorAjuste;
  }

  public static inteiro(inicio: number = 0, fim: number = 1e4): number {
    return RandomUtils.racional(inicio, fim, 0);
  }

  public static texto(
    tamanho: number = RandomUtils.inteiro(10, 50),
    configs?: {
      formato?: "uppercase" | "lowercase" | "titlecase",
      probabilidadeEspaco?: number,
      tamanhoMinimoPalavra?: number,
      caracteres?: string,
    },
  ): string {
    const configsPadrao = {
      formato: "uppercase" as ("uppercase" | "lowercase" | "titlecase"),
      probabilidadeEspaco: 0.1,
      tamanhoMinimoPalavra: 2,
      caracteres: "abcdefghijklmnopqrstuvwxyz",
    }

    if (configs == null) {
      configs = configsPadrao;
    }

    configs = { ...configsPadrao, ...configs };

    let resultado = "";
    let tamanhoPalavra = 0;

    for (let i = 0; i < tamanho; i++) {
      if (Math.random() < configs.probabilidadeEspaco && tamanhoPalavra >= configs.tamanhoMinimoPalavra && tamanho - i + 1 > configs.tamanhoMinimoPalavra) {
        resultado += " ";
        tamanhoPalavra = 0;
        continue;
      }

      resultado += configs.caracteres.charAt(Math.floor(Math.random() * configs.caracteres.length));
      tamanhoPalavra += 1;
    }

    if (configs.formato === "uppercase") {
      resultado = resultado.toUpperCase();
    }
    else if (configs.formato === "lowercase") {
      resultado = resultado.toLowerCase();
    }
    else if (configs.formato === "titlecase") {
      resultado = toTitleCase(resultado);
    }

    return resultado;
  }

  public static booleano(probabilidadeVerdadeiro: number = 0.5): boolean {
    return Math.random() < probabilidadeVerdadeiro;
  }

  public static ua(regional?: string, delegacia?: string): string {
    regional = regional ?? RandomUtils.inteiro(1, 11).toString();
    delegacia = delegacia ?? RandomUtils.texto(3, { probabilidadeEspaco: 0, caracteres: "0123456789" }) + "00";

    return `${regional}${delegacia}`;
  }
}